import * as _ from 'lodash-es';
import type Embed from 'quill/blots/embed';
import type QuillScroll from 'quill/blots/scroll';
import type { ReactElement } from 'react';
import React from 'react';
import { MOD_KEY } from '../../constants/os';
import INTEGRATION_MENTIONS, {
  Behaviors,
} from '../../integrations/IntegrationMentions';
import truncate from '../../utils/text/truncate';
import {
  LINK_TYPE_ATTRIBUTE,
  LINK_TYPES,
  MENTION_TEXT_CLIPBOARD_ATTRIBUTE,
} from '../constants';
import Annotation from './Annotation';
import Comment from './Comment';
import Diff from './Diff';
import * as styles from './Mention.module.scss';
import ReactEmbed from './ReactEmbed';
import Reaction from './Reaction';
import VersionChange from './VersionChange';
import ExternalPostMention from '../mentions/ExternalPostMention';
import GroupMention from '../mentions/GroupMention';
import IntegrationMention from '../mentions/IntegrationMention';
import type { ContainerType } from '../mentions/MentionBase';
import MentionBase, {
  MENTION_CONTAINER_CLASS,
  MentionBaseProvider,
  shouldTruncate,
  TRUNCATION_LENGTHS,
} from '../mentions/MentionBase';
import PostMention from '../mentions/PostMention';
import TopicMention from '../mentions/TopicMention';
import UserMention from '../mentions/UserMention';
import { useQuillReadOnly } from '../QuillContext';
import type { RenderPortalArgs } from '../QuillEditor';
import type { IntegrationMentionType, Mention as MentionType } from '../types';
import buildHTML from '../utils/buildHTML';
import getEditorMentionUrl from '../utils/getEditorMentionUrl';

type MentionCacheUpdater = (value: unknown) => void;

export interface MentionComponentProps {
  id: string;
  link: boolean;
  type: IntegrationMentionType;
  params?: string;
  blot: Embed;
  content: string | null;
  mentionText: string | null;
  updateCache: MentionCacheUpdater;
  selected?: boolean;
  newTab?: boolean;
}

type MentionComponent = (props: MentionComponentProps) => ReactElement;

enum TextAttribute {
  MentionText = 'mention-text',
  Content = 'content',
}

type MentionState = {
  value: MentionType;
} & Record<TextAttribute, string | null>;

const MENTION_TEXT_ATTRIBUTE = 'data-mention-text';

interface WithReadOnlyProps {
  children: (readOnly: boolean) => React.ReactNode;
}

const WithReadOnly = ({ children }: WithReadOnlyProps) =>
  children(useQuillReadOnly());

class Mention extends ReactEmbed<MentionState> {
  static blotName = 'mention';
  static className = 'ql-mention';
  static tagName = 'SPAN';

  static shouldLazyLoad = true;

  static mentionComponents: {
    [type: string]: MentionComponent;
  } = {
    post: PostMention,
    user: UserMention,
    topic: TopicMention,
    group: GroupMention,
    'external-post': ExternalPostMention,
    ...Behaviors.Mention.reduce(
      (ret, name) => ({
        ...ret,
        [INTEGRATION_MENTIONS[name].mentionType]: IntegrationMention,
      }),
      {},
    ),
  };

  static create(value: MentionType) {
    const node = super.create(value) as HTMLSpanElement;
    // the type is used to correctly style the mention before it loaded
    node.setAttribute('data-type', value.type);
    node.classList.add(styles.container);
    return node;
  }

  static formats(domNode: Element) {
    const attributes: {
      content?: string | null;
      'mention-text'?: string | null;
    } = {};

    if (domNode.hasAttribute('data-content')) {
      attributes.content = domNode.getAttribute('data-content');
    }

    if (domNode.hasAttribute(MENTION_TEXT_ATTRIBUTE)) {
      attributes['mention-text'] = domNode.getAttribute(MENTION_TEXT_ATTRIBUTE);
    }

    return _.isEmpty(attributes) ? null : attributes;
  }

  id: string;
  updateCache: MentionCacheUpdater;
  protected defaultNewTab = false;
  protected containerType: ContainerType = 'editor';

  constructor(scroll: QuillScroll, domNode: HTMLElement, value: MentionType) {
    super(scroll, domNode, {
      value,
      [TextAttribute.Content]: null,
      [TextAttribute.MentionText]: null,
    });
    this.id = _.uniqueId();
    this.updateCache = this.format.bind(this, 'content');
    this.contentNode.classList.add(styles.content);

    const openInNewTab = () => {
      const { id, type, params } = value;
      const formats = this.statics.formats(this.domNode) || {};
      const url = getEditorMentionUrl({
        id,
        type,
        content: formats.content,
        params,
      });
      if (url) {
        window.open(url, '_blank', 'noopener');
      }
    };

    domNode.addEventListener('click', (event) => {
      if (!scroll.isEnabled()) return;

      if (event[MOD_KEY] || event.detail === 2) {
        openInNewTab();
      }
    });
  }

  private setText(name: TextAttribute, value: unknown) {
    this.setState({ [name]: value });

    if (!(this.domNode instanceof HTMLElement)) return;

    const domAttribute = `data-${name}`;
    if (typeof value === 'string') {
      this.domNode.setAttribute(domAttribute, value);
      // Set the value on content node to display a placeholder
      // while the mention is loading to prevent flickering.
      this.contentNode.setAttribute(domAttribute, this.truncateIfNeeded(value));
    } else {
      this.domNode.removeAttribute(domAttribute);
      this.contentNode.removeAttribute(domAttribute);
    }
  }

  private truncateIfNeeded(value: string) {
    return shouldTruncate(this.store.getState().value.type)
      ? truncate(value, TRUNCATION_LENGTHS[this.containerType])
      : value;
  }

  format(name: string, value: unknown) {
    if (name === TextAttribute.Content || name === TextAttribute.MentionText) {
      this.setText(name, value);
    } else if (
      (name === Diff.attrName || name === VersionChange.attrName) &&
      this.domNode instanceof HTMLElement &&
      this.domNode.getAttribute(`data-${name}`) !== value
    ) {
      if (value == null) {
        this.domNode.removeAttribute(`data-${name}`);
      } else if (typeof value === 'string') {
        this.domNode.setAttribute(`data-${name}`, value);
      }
    } else if (
      [Comment.attrName, Reaction.attrName, Annotation.attrName].includes(name)
    ) {
      super.format(name, value);
    }
  }

  formatAt(index: number, length: number, name: string, value: unknown) {
    if (
      [
        Comment.attrName,
        Reaction.attrName,
        Annotation.attrName,
        'content',
        'mention-text',
        Diff.attrName,
        VersionChange.attrName,
      ].includes(name)
    ) {
      super.formatAt(index, length, name, value);
    }
  }

  getReferenceNodeForBounds() {
    return (
      this.contentNode.querySelector(`.${MENTION_CONTAINER_CLASS}`) ??
      this.domNode
    );
  }

  html() {
    const {
      value,
      [TextAttribute.Content]: content,
      [TextAttribute.MentionText]: mentionText,
    } = this.store.getState();

    const { id, type, params } = value;
    const url = getEditorMentionUrl({
      id,
      type,
      content: content ?? undefined,
      params,
    });

    if (!url) return '';

    return buildHTML(
      'a',
      {
        [LINK_TYPE_ATTRIBUTE]: LINK_TYPES.mention,
        [MENTION_TEXT_CLIPBOARD_ATTRIBUTE]: mentionText,
        href: url,
      },
      mentionText ?? content ?? url,
    );
  }

  render(
    {
      value,
      [TextAttribute.Content]: content,
      [TextAttribute.MentionText]: mentionText,
    }: MentionState,
    { selected }: RenderPortalArgs,
  ): React.ReactNode {
    const { id, type, params } = value;

    const MentionComponent = Mention.mentionComponents[type];

    if (!MentionComponent) {
      // legacy mentions like clubhouse-story don't have a defined MentionComponent,
      // so we fall back to keep the basic expected behavior (like diffs) working
      return (
        <MentionBaseProvider containerType={this.containerType}>
          <MentionBase
            type="external"
            content={content ?? undefined}
            newTab={this.defaultNewTab}
            selected={selected}
          />
        </MentionBaseProvider>
      );
    }

    return (
      <MentionBaseProvider containerType={this.containerType}>
        <WithReadOnly>
          {(readOnly) => (
            <MentionComponent
              id={id}
              link={readOnly}
              type={type}
              blot={this}
              content={content}
              mentionText={mentionText ?? null}
              updateCache={this.updateCache}
              selected={selected}
              params={params}
              newTab={this.defaultNewTab}
            />
          )}
        </WithReadOnly>
      </MentionBaseProvider>
    );
  }
}

export default Mention;
